import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { ArrowRight, Check, Loader2, FileText } from 'lucide-react';
import InvoiceRequestDialog from '@/components/InvoiceRequestDialog';

type BillingCycle = 'monthly' | 'annual';
type TierId = 'starter' | 'standard' | 'plus';

interface Tier {
  id: TierId;
  name: string;
  description: string;
  monthlyPrice: string;
  annualPrice: string;
  monthlyPriceId: string;
  annualPriceId: string;
  features: string[];
}

const TIERS: Tier[] = [
  {
    id: 'starter',
    name: 'School Starter',
    description: 'Small schools (1-form entry).',
    monthlyPrice: '£49/mo',
    annualPrice: '£490/yr',
    monthlyPriceId: 'price_1TRq1PKHBfrv5p558aMxk9s2',
    annualPriceId: 'price_1TRq1PKHBfrv5p550u2sotCa',
    features: [
      '200 assessments/month',
      'Up to 10 teachers',
      'School-wide pupil records',
      'Multi-page assessment',
      'Automated report comments',
      'Invite & manage teachers',
      'Admin assessment visibility',
      'Key stage management',
    ],
  },
  {
    id: 'standard',
    name: 'School Standard',
    description: 'Average 2-form entry schools.',
    monthlyPrice: '£89/mo',
    annualPrice: '£890/yr',
    monthlyPriceId: 'price_1TRq1PKHBfrv5p55dxh2IL4f',
    annualPriceId: 'price_1TRq1PKHBfrv5p556fMyL98e',
    features: [
      '500 assessments/month',
      'Up to 30 teachers',
      'School-wide pupil records',
      'Multi-page assessment',
      'Automated report comments',
      'Invite & manage teachers',
      'Admin assessment visibility',
      'Key stage management',
    ],
  },
  {
    id: 'plus',
    name: 'School Plus',
    description: 'Large schools (3+ form entry).',
    monthlyPrice: '£149/mo',
    annualPrice: '£1,490/yr',
    monthlyPriceId: 'price_1TRq1PKHBfrv5p55cDKpiRle',
    annualPriceId: 'price_1TRq1PKHBfrv5p55nXrBHQCp',
    features: [
      '1,000 assessments/month',
      'Unlimited teachers',
      'School-wide pupil records',
      'Multi-page assessment',
      'Automated report comments',
      'Invite & manage teachers',
      'Admin assessment visibility',
      'Key stage management',
    ],
  },
];

export default function SchoolBilling() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [billingCycle, setBillingCycle] = useState<BillingCycle>('monthly');
  const [loadingTier, setLoadingTier] = useState<TierId | null>(null);
  const [invoiceDialog, setInvoiceDialog] = useState<{ tier: Tier; priceId: string } | null>(null);

  // Redirect to school setup if the user has no school yet
  useEffect(() => {
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (!session) return;
      const { data: profile } = await supabase
        .from('profiles')
        .select('school_id')
        .eq('user_id', session.user.id)
        .single();
      if (!profile?.school_id) {
        toast({
          title: 'School setup required',
          description: 'Complete your school setup before selecting a plan.',
        });
        navigate('/school-setup');
      }
    });
  }, []);

  const handleCardPayment = async (tier: Tier) => {
    setLoadingTier(tier.id);
    const priceId = billingCycle === 'monthly' ? tier.monthlyPriceId : tier.annualPriceId;
    const { data, error } = await supabase.functions.invoke('stripe-checkout', {
      body: { priceId },
    });
    if (error || !data?.url) {
      toast({
        title: 'Something went wrong',
        description: error?.message ?? 'Unable to start checkout. Please try again.',
        variant: 'destructive',
      });
      setLoadingTier(null);
      return;
    }
    window.location.href = data.url;
  };

  const handleRequestInvoice = (tier: Tier) => {
    const priceId = billingCycle === 'monthly' ? tier.monthlyPriceId : tier.annualPriceId;
    setInvoiceDialog({ tier, priceId });
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6">
      <div className="w-full max-w-4xl space-y-8">
        {/* Header */}
        <div className="text-center space-y-2">
          <h1 className="text-3xl font-bold">Your school is set up.</h1>
          <p className="text-muted-foreground">
            Choose a plan now, or explore WriteAssess first — no card needed to look around.
          </p>
        </div>

        {/* Billing toggle */}
        <div className="flex justify-center">
          <div className="inline-flex items-center rounded-full border bg-muted/40 p-1 gap-1">
            <button
              className={`px-5 py-1.5 rounded-full text-sm font-medium transition-colors ${
                billingCycle === 'monthly'
                  ? 'bg-background shadow text-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
              onClick={() => setBillingCycle('monthly')}
            >
              Monthly
            </button>
            <button
              className={`px-5 py-1.5 rounded-full text-sm font-medium transition-colors ${
                billingCycle === 'annual'
                  ? 'bg-background shadow text-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
              onClick={() => setBillingCycle('annual')}
            >
              Annual
              <span className="ml-2 text-xs text-primary font-semibold">2 months free</span>
            </button>
          </div>
        </div>

        {/* Tier cards */}
        <div className="grid sm:grid-cols-3 gap-4">
          {TIERS.map((tier) => (
            <Card
              key={tier.id}
              className="shadow-soft border-2 border-border hover:border-primary/50 transition-colors flex flex-col"
            >
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">{tier.name}</CardTitle>
                <CardDescription>{tier.description}</CardDescription>
                <div className="mt-2">
                  <span className="text-2xl font-bold">
                    {billingCycle === 'monthly' ? tier.monthlyPrice : tier.annualPrice}
                  </span>
                </div>
              </CardHeader>
              <CardContent className="flex flex-col flex-1 gap-4">
                <ul className="space-y-2 flex-1">
                  {tier.features.map((f) => (
                    <li key={f} className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Check className="h-4 w-4 text-primary shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>

                <div className="flex flex-col gap-2">
                  <Button
                    className="w-full"
                    disabled={loadingTier !== null}
                    onClick={() => handleCardPayment(tier)}
                  >
                    {loadingTier === tier.id ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <ArrowRight className="mr-2 h-4 w-4" />
                    )}
                    Pay by Card
                  </Button>
                  <Button
                    className="w-full"
                    variant="outline"
                    disabled={loadingTier !== null}
                    onClick={() => handleRequestInvoice(tier)}
                  >
                    <FileText className="mr-2 h-4 w-4" />
                    Request Invoice
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Skip — matches the backend: a school with no plan selected stays on an open-ended trial */}
        <div className="text-center space-y-1">
          <Button variant="ghost" onClick={() => navigate('/dashboard')} disabled={loadingTier !== null}>
            Skip for now — explore the dashboard first
          </Button>
          <p className="text-xs text-muted-foreground">
            You can choose a plan any time from Settings → Plan &amp; Usage.
          </p>
        </div>

        {/* VAT notice */}
        <p className="text-center text-xs text-muted-foreground pt-2">
          WriteAssess is not currently VAT registered, so VAT is not charged. Prices shown are the total amount you pay.
        </p>
      </div>

      {invoiceDialog && (
        <InvoiceRequestDialog
          open={true}
          onOpenChange={(open) => { if (!open) setInvoiceDialog(null); }}
          priceId={invoiceDialog.priceId}
          planName={invoiceDialog.tier.name}
        />
      )}
    </div>
  );
}
